import React, { useState } from 'react';
import type { Delai, AppData } from '../types';
import { Button, Card, Input, Select } from './UI';
import { daysUntil, urgencyColor, urgencyLabel } from '../utils/dates';

interface DelaisListProps {
  data: AppData;
  onDataChange: (data: AppData) => void;
}

export function DelaisList({ data, onDataChange }: DelaisListProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Delai>>({});

  const dossiers = data.dossiers;
  const delais = data.delais.sort((a, b) => daysUntil(a.date) - daysUntil(b.date));

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      id: `e${Date.now()}`,
      dossier: dossiers[0]?.id || '',
      titre: '',
      date: new Date().toISOString().split('T')[0],
      type: 'Procédure',
    });
  };

  const handleEdit = (delai: Delai) => {
    setEditingId(delai.id);
    setFormData(delai);
  };

  const handleSave = () => {
    if (!formData.dossier || !formData.titre || !formData.date) {
      alert('Veuillez remplir les champs requis');
      return;
    }

    const newData = { ...data };
    const dossier = newData.dossiers.find((d) => d.id === formData.dossier);

    if (isCreating) {
      newData.delais.push(formData as Delai);
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.dossier!,
        action: 'Délai créé',
        detail: `${formData.titre} — ${formData.date}`,
        ts: new Date().toISOString(),
      });
    } else {
      newData.delais = newData.delais.map((d) =>
        d.id === editingId ? (formData as Delai) : d
      );
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.dossier!,
        action: 'Délai modifié',
        detail: `${formData.titre} — ${formData.date}`,
        ts: new Date().toISOString(),
      });
    }

    onDataChange(newData);
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  const handleDelete = (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce délai ?')) return;

    const delai = data.delais.find((d) => d.id === id);
    const newData = { ...data };
    newData.delais = newData.delais.filter((d) => d.id !== id);
    newData.journal.push({
      id: `j${Date.now()}`,
      dossier: delai?.dossier || '',
      action: 'Délai supprimé',
      detail: `${delai?.titre}`,
      ts: new Date().toISOString(),
    });

    onDataChange(newData);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  if (isCreating || editingId) {
    const dossier = dossiers.find((d) => d.id === formData.dossier);

    return (
      <div>
        <h1 style={{ margin: '0 0 1.5rem', fontSize: 28 }}>
          {isCreating ? 'Créer un délai' : 'Éditer le délai'}
        </h1>

        <Card style={{ maxWidth: 600 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                Dossier *
              </label>
              <Select
                value={formData.dossier || ''}
                onChange={(e) => setFormData({ ...formData, dossier: e.target.value })}
              >
                <option value="">Sélectionner un dossier</option>
                {dossiers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.ref} — {d.client}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                Titre *
              </label>
              <Input
                value={formData.titre || ''}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                placeholder="Ex: Dépôt conclusions"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Date *
                </label>
                <Input
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Type *
                </label>
                <Select
                  value={formData.type || 'Procédure'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                >
                  <option value="Procédure">Procédure</option>
                  <option value="Audience">Audience</option>
                  <option value="Acte">Acte</option>
                </Select>
              </div>
            </div>

            {dossier && (
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: 'var(--border-radius-md)', fontSize: 12 }}>
                <strong>{dossier.ref}</strong> — {dossier.client}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel} variant="secondary">
                Annuler
              </Button>
              <Button onClick={handleSave}>
                {isCreating ? 'Créer' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Délais</h1>
        <Button onClick={handleCreate}>+ Ajouter un délai</Button>
      </div>

      {delais.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {delais.map((delai) => {
            const dossier = dossiers.find((d) => d.id === delai.dossier);
            const daysLeft = daysUntil(delai.date);
            return (
              <Card key={delai.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 0.25rem', fontSize: 13, fontWeight: 500 }}>
                      {delai.titre}
                    </p>
                    <p style={{ margin: '0 0 0.5rem', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      {dossier?.ref} — {dossier?.client}
                    </p>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                      {delai.type} • {delai.date}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'start' }}>
                    <span
                      style={{
                        background: urgencyColor(daysLeft),
                        color: '#ffffff',
                        padding: '4px 10px',
                        borderRadius: 'var(--border-radius-sm)',
                        fontSize: 11,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {urgencyLabel(daysLeft)}
                    </span>
                    <Button onClick={() => handleEdit(delai)} size="sm" variant="secondary">
                      ✎
                    </Button>
                    <Button onClick={() => handleDelete(delai.id)} size="sm" variant="secondary">
                      🗑
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
            Aucun délai enregistré
          </p>
          <Button onClick={handleCreate}>Créer le premier délai</Button>
        </Card>
      )}
    </div>
  );
}
